import { FC, FormEvent, useEffect, useMemo, useState } from 'react';
import TeacherDashboardInfoIcon, { InfoType } from '../TeacherDashboardInfoIcon';
import { Search } from 'lucide-react';
import { AutoComplete, AutoCompleteCompleteEvent } from 'primereact/autocomplete';
import { LessonData } from '../../../custom-hooks/useExitSlipChartValues';
import ExitSlipDateField from '../../ExitSlip/ExitSlipDateField';
import { dueOnDay } from '../../../businessLogicService/exitSlipSnapshotHelper';
import { format, parseISO } from 'date-fns';
import { useAppSelector } from '../../../redux/hooks';
import { selectedTeacherDashboardFilter } from '../../../redux/reducers/teacherDashboardFiltersSlice';

interface Props {
  dateExitSlip: Date;
  setDateExitSlip: (d: Date) => void;
  lessonsData: LessonData[] | [];
}

interface LessonsAvailables {
  id: string;
  name: string;
  number: string;
  date: string;
  label?: string;
}

const ExitSlipSnapshotCardHeader: FC<Props> = ({ dateExitSlip, setDateExitSlip, lessonsData }) => {
  const teacherDashboardFilters = useAppSelector(selectedTeacherDashboardFilter);

  const [lessons, setLessons] = useState<LessonsAvailables[]>([]);
  const [selectedLesson, setSelectedLesson] = useState<LessonsAvailables[] | null>(null);
  const [filteredLessons, setFilteredLessons] = useState<LessonsAvailables[]>([]);

  //a canceled ticket is marked with a dot rather than its lesson number, so it is kept out of
  //the tagged days and handed over separately
  const dayTags = useMemo(
    () =>
      lessonsData
        .filter(el => !el.canceledOn)
        .map(el => ({
          date: dueOnDay(el.dueOn),
          title: `Lesson ${el.curriculumLesson.number}: ${el.curriculumLesson.name}`,
        })),
    [lessonsData],
  );
  const canceledDays = useMemo(
    () => lessonsData.filter(el => el.canceledOn).map(el => dueOnDay(el.dueOn)),
    [lessonsData],
  );

  const searchLessons = (event: AutoCompleteCompleteEvent) => {
    let _filteredLessons;
    if (!event.query.trim().length) {
      _filteredLessons = [...lessons];
    } else {
      _filteredLessons = lessons.filter(el => {
        return el.number.toLowerCase().startsWith(event.query.toLowerCase());
      });
    }

    setFilteredLessons(
      _filteredLessons.map(el => {
        return {
          label: `Lesson ${el.number}: ${el.name}  -  ${el.date}`,
          id: el.id,
          name: el.name,
          number: el.number,
          date: el.date,
        };
      }),
    );
  };

  const itemTemplate = (item: LessonsAvailables) => {
    return (
      <div className="tw-flex tw-items-center tw-w-full">
        <span className="tw-font-bold tw-pr-1">Lesson {item.number}: </span>
        <span className="tw-text-base tw-font-normal lg:tw-inline-block lg:tw-overflow-hidden lg:tw-text-ellipsis">
          {item.name}
        </span>
        <span className="tw-text-sm tw-font-bold tw-text-neutral-500 tw-ml-auto">{item.date}</span>
      </div>
    );
  };

  useEffect(() => {
    setLessons(
      lessonsData
        .sort((a, b) => new Date(b.dueOn).getTime() - new Date(a.dueOn).getTime())
        .map(el => {
          return {
            id: el.id,
            name: el.curriculumLesson.name,
            number: el.curriculumLesson.number,
            date: format(parseISO(el.dueOn), 'MM/dd/yyyy'),
          };
        }),
    );
  }, [lessonsData]);

  useEffect(() => {
    setSelectedLesson(null);
    setDateExitSlip(new Date());
  }, [teacherDashboardFilters.classes]);

  return (
    <div className="tw-w-full tw-flex tw-flex-col tw-gap-y-3">
      <div className="tw-flex tw-flex-row tw-justify-between">
        <div className="tw-flex tw-items-center tw-gap-2.5">
          <h1 className="tw-font-atkinson tw-text-lg/12 tw-font-bold">Exit Tickets</h1>
          <span className="tw-whitespace-nowrap tw-rounded-lg tw-border-2 tw-border-solid tw-border-neutral-200 tw-bg-neutral-100 tw-px-2.5 tw-py-0.5 tw-font-atkinson tw-text-xs tw-font-bold tw-leading-4 tw-text-neutral-900">
            Core instruction
          </span>
        </div>
        <TeacherDashboardInfoIcon infoType={InfoType.EXIT_SLIP} />
      </div>
      <div className="tw-h-px tw-bg-neutral-200" />
      <div className="tw-flex tw-flex-col tw-gap-4 lg:tw-flex-row lg:tw-items-center">
        <ExitSlipDateField
          value={new Date(dateExitSlip)}
          onChange={date => {
            setDateExitSlip(new Date(date));
            setSelectedLesson([]);
          }}
          schedule={dayTags}
          canceled={canceledDays}
          blocked={[]}
          pastTags={[]}
          selectablePast
          className="!tw-w-full lg:!tw-w-40"
        />
        <div className="tw-min-w-0 tw-flex-1">
          <AutoComplete
            pt={{
              root: {
                className:
                  '!tw-flex !tw-w-full !tw-items-center !tw-gap-2 !tw-rounded-full !tw-border-2 !tw-border-solid !tw-border-neutral-100 !tw-bg-white !tw-px-4 !tw-py-2 !tw-font-atkinson',
              },
              container: { className: '!tw-m-0 !tw-min-w-0 !tw-flex-1 !tw-border-none !tw-bg-transparent !tw-p-0' },
              input: {
                className: '!tw-font-atkinson !tw-text-sm !tw-leading-5 !tw-text-neutral-700',
                inputMode: 'decimal',
                onBeforeInput: (event: FormEvent<HTMLInputElement>) => {
                  const { data } = event.nativeEvent as InputEvent;
                  if (data && !/^[\d.]+$/.test(data)) {
                    event.preventDefault();
                  }
                },
              },
              dropdownButton: {
                root: {
                  className:
                    '!tw-order-first !tw-size-5 !tw-shrink-0 !tw-border-none !tw-bg-transparent !tw-p-0 !tw-text-neutral-700',
                },
              },
              emptyMessage: { className: '!tw-text-sm !tw-font-bold !tw-text-neutral-500 !tw-ml-auto' },
            }}
            placeholder="Search by lesson number"
            className="tw-w-full nc_exit-slip-snapshot-search-bar"
            field="label"
            appendTo="self"
            inputClassName="tw-w-full"
            panelClassName="tw-w-full tw-max-w-full"
            value={selectedLesson}
            suggestions={filteredLessons}
            completeMethod={searchLessons}
            onChange={e => setSelectedLesson(e.value)}
            onSelect={e => {
              if (e.value.date) {
                setSelectedLesson([e.value]);
                setDateExitSlip(e.value.date);
              }
            }}
            itemTemplate={itemTemplate}
            dropdown
            dropdownIcon={<Search size={20} />}
            multiple
            selectionLimit={1}
            showEmptyMessage
            emptyMessage="No results were found"
          />
        </div>
      </div>
    </div>
  );
};

export default ExitSlipSnapshotCardHeader;
